import { useCalculationFields, useVariablesData } from "@ploy-lib/calculation";
import { useEffect, useMemo, useState } from "react";
import isEqual from "lodash/isEqual";
import { getIn, useFormikContext } from "formik";
import { isNotNull } from "../../../utils";
export const toFakeCamelCase = (field) => {
    return field.charAt(0).toLowerCase() + field.slice(1);
};
export const filterAndStripEntriesStartingWith = (stringToMatchAndRemove, values) => values
    .filter(([name, _]) => name.startsWith(stringToMatchAndRemove))
    .map(([name, value]) => [
    name.substring(stringToMatchAndRemove.length),
    value
]);
const defaultNameMaker = (col) => `${col.tableType}.${col.name}`;
export const useOptionSourceFields = (columnsWithOptionSource, nameMaker = defaultNameMaker) => {
    const [optionSourceUnderlyingVariables, optionSourceFields] = useMemo(() => [
        columnsWithOptionSource.map(col => ({
            variableName: col.optionSource?.fieldName ?? "",
            namespace: col.optionSource?.namespace ?? ""
        })),
        columnsWithOptionSource.map(col => ({
            name: col.optionSource?.fieldName ?? "",
            namespace: col.optionSource?.namespace ?? ""
        }))
    ], [columnsWithOptionSource]);
    const optionSourceCalcUnderlyingVariables = useVariablesData(...optionSourceUnderlyingVariables);
    const optionSourceCalcFields = useCalculationFields(...optionSourceFields);
    const { values } = useFormikContext();
    const optionSourceFormikFields = useMemo(() => columnsWithOptionSource.map(col => ({
        variableName: col.optionSource?.fieldName,
        value: getIn(values, col.optionSource?.fieldName || "")
    })), [values, columnsWithOptionSource]);
    const [optionSources, setOptionSources] = useState({});
    useEffect(() => {
        if (!optionSourceCalcFields || !optionSourceFormikFields)
            return;
        const candidate = columnsWithOptionSource.reduce((acc, col) => {
            const value = [
                ...optionSourceCalcUnderlyingVariables,
                ...optionSourceCalcFields,
                ...optionSourceFormikFields
            ]
                .filter(isNotNull)
                .find(field => Array.isArray(field.value) &&
                field.variableName === col.optionSource?.fieldName)?.value;
            acc[nameMaker(col)] = (Array.isArray(value) ? value : []).map(fieldValue => ({
                key: fieldValue[toFakeCamelCase(col.optionSource?.key ?? "")] ?? "",
                value: fieldValue[toFakeCamelCase(col.optionSource?.value ?? "")] ?? ""
            }));
            return acc;
        }, {});
        if (!isEqual(optionSources, candidate)) {
            setOptionSources(candidate);
        }
    }, [
        columnsWithOptionSource,
        nameMaker,
        optionSourceCalcFields,
        optionSourceCalcUnderlyingVariables,
        optionSourceFormikFields,
        optionSources
    ]);
    return optionSources;
};
export function ensureStringValues(values) {
    return values.map(({ key, value }) => ({
        key: key.toString(),
        value: value.toString()
    }));
}
export const escapeFormikName = (name) => name.split(".").join("___");
export const unescapeFormikName = (name) => name.split("___").join(".");
export const isNumeric = (value) => {
    if (value && Array.isArray(value))
        return false;
    return !isNaN(parseFloat(value?.toString()));
};
export const isDate = (value) => {
    if (!value || Array.isArray(value))
        return false;
    // Some value like "Kirkegata 8" will return `Wed Aug 01 2001 00:00:00` (i.e. a valid date) for `new Date("Kirkegata 8")`.
    // Idk why, but such a string-format with "<string><space><number>" is treated as a valid date for JS.
    // However, if the string actually starts with the same first 3 letters as a month, that month is used for the date
    //		(i.e. all these values: "Sep 16", "September 16", "Separation 16", and "Seppuku looks painful 16" will all set the date `Sun Sep 16 2001 00:00:00`)
    // So - additionally we need to check whether the input (as string) starts with a word (non-digits)
    //		and if so, whether the input corresponds to a month
    const firstWord = value.toString().split(" ")[0];
    const regex_noDigitsInWord = new RegExp(/^\D+$/);
    const monthDict = {
        jan: 1,
        feb: 2,
        mar: 3,
        apr: 4,
        may: 5,
        jun: 6,
        jul: 7,
        aug: 8,
        sep: 9,
        oct: 10,
        nov: 11,
        dec: 12
    };
    // This validation will still treat "Seppuku" as "September" and thus as a valid date-value.
    if (regex_noDigitsInWord.test(firstWord) &&
        monthDict[firstWord.toLowerCase().substring(0, 3)] > 0)
        return false;
    return new Date(value).getMonth() >= 0;
};
//# sourceMappingURL=utils.js.map